import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import PageHero, { HeroCtas } from '../components/ui/PageHero';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { SectionHead } from '../components/ui/SectionHead';
import Notice from '../components/ui/Notice';
import StageList from '../components/ui/StageList';
import Button from '../components/ui/Button';
import {
  CriteriaSchedule,
  DecisionStops,
  LensList,
  RoleMatrix,
  MaterialPair,
  PillarClose,
  formatEvidence,
} from '../components/ui/PillarBlocks';
import { serviceSchema, faqSchema, breadcrumbSchema } from '../components/ui/schema';

const PropertyDevelopment = () => {
  const { t, i18n } = useTranslation('cy');
  const meta = t('property.meta', { returnObjects: true });
  const faqItems = t('property.faq.items', { returnObjects: true });

  const assessmentItems = t('property.assessment.rows', { returnObjects: true }).map((r) => ({
    title: r.face,
    gate: Boolean(r.gate),
    fields: [
      { label: t('ui.cols.input'), value: r.input },
      { label: t('ui.cols.evidence'), value: formatEvidence(r.evidence), tone: 'evidence' },
      { label: t('ui.cols.impact'), value: r.impact, tone: 'judgement' },
    ],
  }));

  const schema = [
    serviceSchema(t('nav.pillars.property.label'), meta.description),
    faqSchema(faqItems),
    breadcrumbSchema(
      [{ label: t('nav.home'), to: '/' }, { label: t('nav.pillars.property.label'), to: '/property-development' }],
      i18n.language
    ),
  ];

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/property-development" schema={schema} />

      <PageHero
        id="property-hero"
        eyebrow={t('property.hero.eyebrow')}
        title={t('property.hero.h1')}
        kicker={t('property.hero.kicker')}
        breadcrumb={<Breadcrumbs items={[{ label: t('nav.pillars.property.label') }]} />}
      >
        <Notice variant="inline" className="cy-hero__boundary">
          {t('property.hero.boundary')} {t('property.hero.roleNote')}{' '}
          <Link className="cy-link" to="/terms#terms-investment-boundary">{t('ui.terms')}</Link>
        </Notice>
        <HeroCtas
          primary={t('property.hero.cta')}
          primaryTo="/submit-opportunity?type=property"
          secondary={t('property.hero.seeCriteria')}
          secondaryTo="#property-fit"
        />
      </PageHero>

      <section className="cy-section cy-section--alt cy-zone--doc" id="property-fit">
        <div className="cy-container">
          <SectionHead title={t('property.fit.h2')} lead={t('property.fit.lead')} />
          <CriteriaSchedule
            rows={t('property.fit.rows', { returnObjects: true })}
            term="dim"
            primary="what"
            secondary="why"
          />
          <p className="cy-body cy-section-note">
            {t('property.fit.note')}{' '}
            <Link className="cy-link" to="#property-materials">{t('property.fit.prepareLink')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-zone--doc" id="property-not-fit">
        <div className="cy-container">
          <SectionHead title={t('property.notFit.h2')} lead={t('property.notFit.lead')} />
          <DecisionStops items={t('property.notFit.items', { returnObjects: true })} featured />
          <Notice variant="compliance" className="cy-notice--inline cy-section-note">
            {t('property.notFit.boundary')}
          </Notice>
          <p className="cy-section-action">
            <Link className="cy-link" to="/contact">{t('property.notFit.alt')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-section--alt cy-zone--doc" id="property-roles">
        <div className="cy-container">
          <SectionHead title={t('property.roles.h2')} lead={t('property.roles.lead')} />
          <RoleMatrix
            rows={t('property.roles.rows', { returnObjects: true })}
            titleKey="role"
            fields={[
              { key: 'when', label: t('ui.cols.when') },
              { key: 'responsibility', label: t('ui.cols.responsibility') },
            ]}
            notKey="notMeaning"
            notLabel={t('ui.cols.notMeaning')}
          />
          <Notice variant="compliance" className="cy-section-note">
            {t('property.roles.note')}{' '}
            <Link className="cy-link" to="/approach#approach-roles">{t('property.roles.methodLink')}</Link>
          </Notice>
        </div>
      </section>

      <section className="cy-section cy-zone--doc" id="property-assessment">
        <div className="cy-container">
          <SectionHead eyebrow={t('property.assessment.eyebrow')} title={t('property.assessment.h2')} lead={t('property.assessment.lead')} />
          <LensList items={assessmentItems} gateLabel={t('ui.cols.gate')} />
          <p className="cy-body cy-section-note">{t('property.assessment.lens')}</p>
          <p className="cy-fineprint cy-section-note">
            {t('property.assessment.gateNote')}{' '}
            <Link className="cy-link" to="/approach#approach-gates">{t('property.assessment.gateLink')}</Link>
          </p>
          <p className="cy-section-action">
            <Link className="cy-link" to="/approach#approach-stages">{t('property.assessment.cta')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-section--alt cy-zone--doc" id="property-materials">
        <div className="cy-container">
          <SectionHead title={t('property.materials.h2')} lead={t('property.materials.lead')} />
          <MaterialPair
            minimumTitle={t('property.materials.minimumTitle')}
            laterTitle={t('property.materials.laterTitle')}
            minimum={t('property.materials.minimum', { returnObjects: true })}
            later={t('property.materials.later', { returnObjects: true })}
          />
          <Notice variant="inline" className="cy-section-note">
            {t('property.materials.privacy')}{' '}
            <Link className="cy-link" to="/privacy#privacy-collection">{t('ui.privacyPolicy')}</Link>
          </Notice>
          <div className="cy-section-action">
            <Button to="/submit-opportunity?type=property">{t('property.materials.cta')}</Button>
          </div>
        </div>
      </section>

      <section className="cy-section cy-zone--doc" id="property-intermediaries">
        <div className="cy-container">
          <SectionHead title={t('property.intermediaries.h2')} lead={t('property.intermediaries.lead')} />
          <CriteriaSchedule
            rows={t('property.intermediaries.rows', { returnObjects: true })}
            term="point"
            primary="detail"
            secondary="why"
          />
          <p className="cy-fineprint cy-section-note">{t('property.intermediaries.note')}</p>
          <p className="cy-section-action">
            <Link className="cy-link" to="/approach#approach-risk">{t('property.intermediaries.cta')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-section--alt cy-zone--doc" id="property-path">
        <div className="cy-container">
          <SectionHead title={t('property.path.h2')} lead={t('property.path.lead')} />
          <StageList items={t('property.path.steps', { returnObjects: true })} />
          <p className="cy-fineprint cy-section-note">{t('property.path.note')}</p>
          <p className="cy-section-action">
            <Link className="cy-link" to="/approach#approach-stages">{t('property.path.cta')}</Link>
          </p>
        </div>
      </section>

      <PillarClose
        faqId="property-faq"
        ctaId="property-faq-cta"
        faqTitle={t('property.faq.h2')}
        faqItems={faqItems}
        idPrefix="property-faq"
        cta={t('property.faq.cta')}
        ctaTo="/submit-opportunity?type=property"
        alt={t('property.faq.alt')}
        altTo="/contact"
        siblings={t('property.faq.siblings', { returnObjects: true })}
      />
    </Layout>
  );
};

export default PropertyDevelopment;
